import dayjs, { ManipulateType } from "dayjs";
import PolicyRequired from "enums/PolicyRequired";
import {
  CardPolicy,
  getCardPolicyInitialValues,
  Merchant,
} from "utils/cards/cardFormUtils";

interface NamedEntity {
  id: string;
  name: string;
}

interface CardPresetTimeFrame {
  timeFrameValue?: number | null;
  timeFrameFrequency?: ManipulateType | null;
}

/** A saved card preset, with its limits as numbers. */
export interface CardPreset {
  id: string;
  presetName: string;
  transactionLimit?: number;
  dailyLimit?: number;
  weeklyLimit?: number;
  monthlyLimit?: number;
  totalLimit?: number;
  allowedMerchants?: Merchant[];
  allowedCategories?: NamedEntity[];
  blockedMerchants?: Merchant[];
  blockedCategories?: NamedEntity[];
  allocationType?: { key?: string; name?: string };
  allocationCode?: { id?: string; allocationCode?: string };
  expenseCategory?: { id?: string; name?: string };
  allocationPolicy?: PolicyRequired;
  receiptPolicy?: PolicyRequired;
  cancelationTimeFrame?: CardPresetTimeFrame;
}

export interface CardPresetNode {
  id: string;
  presetName: string;
  transactionLimit?: number | null;
  dailyLimit?: number | null;
  weeklyLimit?: number | null;
  monthlyLimit?: number | null;
  totalLimit?: number | null;
  expenseCategoryData?: { id?: string | null; name?: string | null } | null;
  allocationData?: {
    key?: string | null;
    name?: string | null;
    id?: string | null;
    allocationCode?: string | null;
  } | null;
  receiptPolicy?: string | null;
  allocationPolicy?: string | null;
  cancellationTimeFrame?: {
    timeFrameValue?: number | null;
    timeFrameFrequency?: string | null;
  } | null;
  allowedMerchants?: Merchant[] | null;
  allowedCategories?: NamedEntity[] | null;
  blockedMerchants?: Merchant[] | null;
  blockedCategories?: NamedEntity[] | null;
}

const FREQUENCIES: ManipulateType[] = ["day", "week", "month"];

const toFrequency = (value?: string | null): ManipulateType | null =>
  FREQUENCIES.find((frequency) => frequency === value) ?? null;

const toPolicy = (value?: string | null): PolicyRequired =>
  Object.values(PolicyRequired).find((policy) => policy === value) ??
  PolicyRequired.policy;

/** Empty and non-numeric become undefined, so the caption skips them. */
const toAmount = (value?: string | number | null): number | undefined => {
  const parsed = typeof value === "number" ? value : parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : undefined;
};

// CardPolicy holds limits as strings, off number inputs.
const toLimit = (value?: number): string =>
  value === undefined ? "" : String(value);

export const cardPresetFromNode = (node: CardPresetNode): CardPreset => ({
  id: node.id,
  presetName: node.presetName,
  transactionLimit: toAmount(node.transactionLimit),
  dailyLimit: toAmount(node.dailyLimit),
  weeklyLimit: toAmount(node.weeklyLimit),
  monthlyLimit: toAmount(node.monthlyLimit),
  totalLimit: toAmount(node.totalLimit),
  allowedMerchants: node.allowedMerchants ?? [],
  allowedCategories: node.allowedCategories ?? [],
  blockedMerchants: node.blockedMerchants ?? [],
  blockedCategories: node.blockedCategories ?? [],
  allocationType: {
    key: node.allocationData?.key ?? undefined,
    name: node.allocationData?.name ?? undefined,
  },
  allocationCode: {
    id: node.allocationData?.id ?? undefined,
    allocationCode: node.allocationData?.allocationCode ?? undefined,
  },
  expenseCategory: {
    id: node.expenseCategoryData?.id ?? undefined,
    name: node.expenseCategoryData?.name ?? undefined,
  },
  allocationPolicy: toPolicy(node.allocationPolicy),
  receiptPolicy: toPolicy(node.receiptPolicy),
  cancelationTimeFrame: {
    timeFrameValue: node.cancellationTimeFrame?.timeFrameValue ?? null,
    timeFrameFrequency: toFrequency(
      node.cancellationTimeFrame?.timeFrameFrequency,
    ),
  },
});

/** A preset built from the preset drawer's form, for the picker to select once saved. */
export const cardPresetFromPolicy = (
  policy: CardPolicy,
  id: string,
  presetName: string,
): CardPreset => ({
  id,
  presetName,
  transactionLimit: toAmount(policy.transactionLimit),
  dailyLimit: toAmount(policy.dailyLimit),
  weeklyLimit: toAmount(policy.weeklyLimit),
  monthlyLimit: toAmount(policy.monthlyLimit),
  totalLimit: toAmount(policy.totalLimit),
  allowedMerchants: policy.allowedMerchants,
  allowedCategories: policy.allowedCategories,
  blockedMerchants: policy.blockedMerchants,
  blockedCategories: policy.blockedCategories,
  allocationType: policy.allocationType,
  allocationCode: policy.allocationCode,
  expenseCategory: policy.expenseCategory,
  allocationPolicy: policy.allocationPolicy,
  receiptPolicy: policy.receiptPolicy,
  cancelationTimeFrame: {
    timeFrameValue: policy.cancelationTimeFrame?.timeFrameValue ?? null,
    timeFrameFrequency: policy.cancelationTimeFrame?.timeFrameFrequency ?? null,
  },
});

/** "$500/Transaction, $2,000/Month": the preset's limits on one line. */
export const presetLimitsCaption = (preset: CardPreset): string => {
  const money = (value: number) => `$${value.toLocaleString()}`;
  const parts: string[] = [];
  if (preset.transactionLimit)
    parts.push(`${money(preset.transactionLimit)}/Transaction`);
  if (preset.dailyLimit) parts.push(`${money(preset.dailyLimit)}/Day`);
  if (preset.weeklyLimit) parts.push(`${money(preset.weeklyLimit)}/Week`);
  if (preset.monthlyLimit) parts.push(`${money(preset.monthlyLimit)}/Month`);
  if (preset.totalLimit) parts.push(`Total: ${money(preset.totalLimit)}`);
  return parts.join(", ");
};

/** Undoes a preset: every field a preset sets goes back to its default. */
export const clearCardPreset = (policy: CardPolicy): CardPolicy => ({
  ...getCardPolicyInitialValues(),
  customFields: policy.customFields,
});

// A card has no time frame of its own, so a preset's becomes the date it lands on.
const cancelationDateAfter = (
  timeFrame?: CardPresetTimeFrame,
): number | null => {
  const value = timeFrame?.timeFrameValue;
  const frequency = timeFrame?.timeFrameFrequency;
  return value && frequency
    ? dayjs().add(value, frequency).endOf("day").valueOf()
    : null;
};

/** Replaces every field a preset sets, so nothing from an earlier preset survives. */
export const applyCardPreset = (
  policy: CardPolicy,
  preset: CardPreset,
): CardPolicy => ({
  ...clearCardPreset(policy),
  presetId: preset.id,
  nameOfPreset: preset.presetName,
  transactionLimit: toLimit(preset.transactionLimit),
  dailyLimit: toLimit(preset.dailyLimit),
  weeklyLimit: toLimit(preset.weeklyLimit),
  monthlyLimit: toLimit(preset.monthlyLimit),
  totalLimit: toLimit(preset.totalLimit),
  allowedMerchants: preset.allowedMerchants ?? [],
  allowedCategories: preset.allowedCategories ?? [],
  blockedMerchants: preset.blockedMerchants ?? [],
  blockedCategories: preset.blockedCategories ?? [],
  expenseCategory: {
    id: preset.expenseCategory?.id ?? "",
    name: preset.expenseCategory?.name ?? "",
    level: 0,
  },
  allocationType: {
    key: preset.allocationType?.key ?? "",
    name: preset.allocationType?.name ?? "",
  },
  allocationCode: {
    id: preset.allocationCode?.id ?? "",
    allocationCode: preset.allocationCode?.allocationCode ?? "",
  },
  receiptPolicy: preset.receiptPolicy ?? PolicyRequired.policy,
  allocationPolicy: preset.allocationPolicy ?? PolicyRequired.policy,
  cancelationDate: cancelationDateAfter(preset.cancelationTimeFrame),
});
